import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  fetchAllUserType,
  userTypeItem,
  UpdateAccessPermissionService,
  UpdateAccessPermissionPayload,
} from "../Services/APIService";

interface accessPermissions {
  permissionId: number;
  userTypeId: number;
  menuId: number;
  isAccessAllowed: boolean;
  menuName: string;
  parentMenuName: string;
  isMenu: boolean;
  isSubMenu: boolean;
}

interface UpdateCardProps {
  accessPermissionData: accessPermissions | null;
  onUpdateComplete: () => void;
  onCancel: () => void;
}

interface FormData {
  permissionId: number | "";
  userTypeId: number;
  isAccessible: boolean;
  menuName: string;
  parentMenuName: string;
  isMenu: boolean;
}

const UpdateCard: React.FC<UpdateCardProps> = ({
  accessPermissionData,
  onUpdateComplete,
  onCancel,
}) => {
  const [userTypes, setUserTypes]                   = useState<userTypeItem[]>([]);
  const [isLoadingUserTypes, setIsLoadingUserTypes] = useState<boolean>(true);
  const [isLoading, setIsLoading]                   = useState(false);
  const [error, setError]                           = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    permissionId:   "",
    userTypeId:     1,
    isAccessible:   true,
    menuName:       "",
    parentMenuName: "",
    isMenu:         false,
  });

  // Fetch user types on mount
  useEffect(() => {
    const getUserTypes = async () => {
      try {
        setIsLoadingUserTypes(true);
        const types = await fetchAllUserType();
        if (types && types.length > 0) {
          setUserTypes(types);
        } else {
          setError("No user types found");
        }
      } catch {
        setError("Failed to load user types");
      } finally {
        setIsLoadingUserTypes(false);
      }
    };
    getUserTypes();
  }, []);

  // Populate form when accessPermissionData + userTypes are ready
  useEffect(() => {
    if (accessPermissionData && userTypes.length > 0) {
      setFormData({
        permissionId:   accessPermissionData.permissionId,
        userTypeId:     accessPermissionData.userTypeId,
        isAccessible:   accessPermissionData.isAccessAllowed,
        menuName:       accessPermissionData.menuName,
        parentMenuName: accessPermissionData.parentMenuName,
        isMenu:         accessPermissionData.isMenu,
      });
    }
  }, [accessPermissionData, userTypes]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleUserTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, userTypeId: parseInt(e.target.value) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.permissionId === "" || isNaN(Number(formData.permissionId))) {
      setError("Please enter a valid Permission ID");
      return;
    }

    const userId   = localStorage.getItem("UserId");
    const tenantId = localStorage.getItem("tenantId");

    if (!userId || !tenantId) {
      setError("User ID or Tenant ID not found. Please log in again.");
      return;
    }

    const payload: UpdateAccessPermissionPayload = {
      permissionId: Number(formData.permissionId),
      tenantId:     parseInt(tenantId),
      isAccessible: formData.isAccessible,
      modifiedBy:   parseInt(userId),
      userTypeId:   formData.userTypeId,
    };

    try {
      setIsLoading(true);
      const result = await UpdateAccessPermissionService(payload);

      if (result.isUpdated) {
        toast.success("Access permission updated successfully!");
        onUpdateComplete();
      } else {
        throw new Error(result.message || "Failed to update access permission");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onCancel();
  };

  return (
    <div className="card">
      <Toaster position="top-center" reverseOrder={false} containerStyle={{ zIndex: 10000 }} />

      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Update Access Permission</h5>
      </div>

      <div className="card-body">
        {isLoadingUserTypes ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading user types...</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-danger text-danger" role="alert">
                {error}
              </div>
            )}

            <div className="row">
              {/* Permission ID */}
              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="permissionId" className="form-label">
                  Permission ID
                </label>
                <input
                  type="number"
                  id="permissionId"
                  name="permissionId"
                  className="form-control form-control-lg"
                  placeholder="Permission ID"
                  value={formData.permissionId}
                  disabled
                  required
                />
              </div>

              {/* User Type */}
              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="userType" className="form-label">
                  User Type
                </label>
                <select
                  id="userType"
                  name="userTypeId"
                  className="form-select form-control-lg"
                  value={formData.userTypeId}
                  onChange={handleUserTypeChange}
                  required
                  disabled={userTypes.length === 0}
                >
                  {userTypes.length > 0 ? (
                    userTypes.map((type) => (
                      <option key={type.typeId} value={type.typeId}>
                        {type.typeName}
                      </option>
                    ))
                  ) : (
                    <option value="">No user types available</option>
                  )}
                </select>
              </div>

              {/* Menu */}
              <div className="col-12 col-lg-6 mb-3">
                <label className="form-label">Menu</label>
                <input
                  type="text"
                  className="form-control form-control-lg"
                  value={formData.parentMenuName || formData.menuName || ""}
                  disabled
                />
              </div>

              {/* Sub Menu — only shown if not a top-level menu */}
              {!formData.isMenu && (
                <div className="col-12 col-lg-6 mb-3">
                  <label className="form-label">Sub Menu</label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    value={formData.isMenu ? formData.parentMenuName || "" : formData.menuName || ""}
                    disabled
                  />
                </div>
              )}

              {/* Access Allowed checkbox */}
              <div className={!formData.isMenu ? "col-12 col-lg-6 mb-3" : "col-12 col-lg-12 mb-3"}>
                <label className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="isAccessible"
                    checked={formData.isAccessible}
                    onChange={handleCheckboxChange}
                  />
                  <span className="form-check-label">Access Allowed</span>
                </label>
              </div>

              {/* Actions */}
              <div className="col-12 mt-2">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={isLoading || userTypes.length === 0}
                >
                  {isLoading ? "Updating..." : "Update"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UpdateCard;